import type {
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteRecordRaw,
  LocationQuery,
} from "vue-router";
import type { GetDocumentByIdQuery } from "@/graphql/operations";
import { classicThemeRouteMeta } from "./classicTheme";

/** Compatibility only. These routes never render the retired Modern UI. */
export const LEGACY_MODERN_PRESENTER = "LegacyModernPresenter";

function selectionQuery(query: LocationQuery, keys: string[]) {
  return Object.fromEntries(
    keys
      .filter((key) => typeof query[key] === "string")
      .map((key) => [key, query[key]]),
  );
}

function redirect(
  path: string,
  name: string,
  params: Record<string, string> = {},
  queryKeys: string[] = [],
): RouteRecordRaw {
  return {
    path,
    meta: { requiresAuth: true, ...classicThemeRouteMeta },
    redirect: (to) => ({
      name,
      params: Object.fromEntries(
        Object.entries(params).map(([target, source]) => [
          target,
          to.params[source],
        ]),
      ),
      query: selectionQuery(to.query, queryKeys),
    }),
  };
}

export const legacyModernRoutes: RouteRecordRaw[] = [
  redirect("/modern", "WorkspaceList"),
  redirect("/modern/workspaces", "WorkspaceList"),
  redirect(
    "/modern/workspaces/:workspaceId",
    "Workspace",
    { workspaceId: "workspaceId" },
    ["fid", "page"],
  ),
  redirect("/modern/shared", "DocumentSpaces"),
  redirect("/modern/shared/:spaceId", "DocumentSpace", { id: "spaceId" }),
  redirect("/modern/flows", "CustomFlows"),
  redirect("/modern/flows/:flowId", "CustomFlow", { id: "flowId" }),
  redirect("/modern/settings", "SettingsOverview"),
  redirect("/modern/settings/profile", "Profile"),
  redirect("/modern/settings/teams", "TeamList"),
  redirect("/modern/settings/shared-spaces", "SettingsOverview"),
  redirect("/modern/settings/templates/:templateId", "TemplateEditor", {
    id: "templateId",
  }),
  redirect(
    "/modern/hcp-uploads/:hcpWorkspaceId?",
    "HcpInbox",
    { hcpWorkspaceId: "hcpWorkspaceId" },
    ["file"],
  ),
  {
    path: "/modern/present/:documentId",
    name: LEGACY_MODERN_PRESENTER,
    meta: { requiresAuth: true, ...classicThemeRouteMeta },
    // Resolved by the normal auth guard, after user and account readiness.
    // Keeping a route record avoids any dependence on old browser history.state.
    component: { render: () => null },
  },
];

export function legacyDocumentTarget(
  document: GetDocumentByIdQuery["document"] | undefined,
  route: Pick<RouteLocationNormalized, "params" | "query">,
): RouteLocationRaw {
  if (
    !document ||
    document.id !== route.params.documentId ||
    document.deletedAt ||
    !document.firestoreId
  )
    return { name: "PageNotFound", replace: true };

  if (document.rootWorkspace?.isHcpUploadWorkspace) {
    if (!document.rootWorkspace.firebaseId)
      return { name: "PageNotFound", replace: true };
    return {
      name: "HcpInbox",
      params: { hcpWorkspaceId: document.rootWorkspace.firebaseId },
      query: { file: document.firestoreId },
      replace: true,
    };
  }

  if (!document.workspace?.id) return { name: "PageNotFound", replace: true };

  return {
    name: "StackedProjector",
    params: { workspaceId: document.workspace.id },
    query: {
      ...selectionQuery(route.query, ["page"]),
      fid: document.firestoreId,
    },
    replace: true,
  };
}
